import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CookieService} from "ngx-cookie-service";
import {GraphService} from "../../shared/services/graph.service";
import {Subscription} from "rxjs";
import {GraphVariableModel} from "../../shared/models/graph-variable.model";

@Component({
  selector: 'app-graph-data-transformers',
  templateUrl: './graph-data-transformers.component.html',
  styleUrls: ['./graph-data-transformers.component.scss']
})
export class GraphDataTransformersComponent implements OnInit, OnDestroy {
  @Input() componentContext: 'graph-page' | 'dataset-page';
  interpolate: string;
  multiAnnualMean: string;
  dataTransform: string;
  graphType: string;
  disableMultiAnnual: boolean;
  graphVariablesSubscription: Subscription;
  graphTypeSubscription: Subscription;
  graphVariables: GraphVariableModel[];

  constructor(
    private graphService: GraphService
  ) {
  }

  ngOnInit(): void {
    this.onInitDataFromLocalStorage();
    this.subscribeToGraphVariables();
    this.subscribeToGraphType();
  }

  /**
   * A transform flag is "on" only when a non-empty string was actually stored.
   * getLocalStorageSelectedVariables() returns [] for an absent key, and [] is
   * truthy — which previously made Interpolation/Multi Annual Mean appear selected
   * (and silently applied) by default. Treat anything that isn't a non-empty string
   * as off.
   */
  private isStoredFlagOn(value: any): boolean {
    return typeof value === 'string' && value.length > 0;
  }

  onInitDataFromLocalStorage(): void {
    const graphVariablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    this.graphVariables = this.graphService.getLocalStorageSelectedVariables(graphVariablesKey);

    const dataTransformKey = this.graphService.getDataTransformGraphKey(this.componentContext);
    const dataTransform = this.graphService.getLocalStorageSelectedVariables(dataTransformKey);
    // Normalize the absent-key [] to '' so the chip listbox isn't bound to an array.
    this.dataTransform = typeof dataTransform === 'string' ? dataTransform : '';

    const graphTypeKey = this.graphService.getGraphTypeKey(this.componentContext);
    const graphType = this.graphService.getLocalStorageSelectedVariables(graphTypeKey);
    // getLocalStorageSelectedVariables() already JSON.parses; do not parse again (crashes on reload).
    this.graphType = graphType.length > 0 ? graphType : null;

    const interpolateKey = this.graphService.getDataTransformInterpolateKey(this.componentContext);
    const interpolate = this.graphService.getLocalStorageSelectedVariables(interpolateKey);
    this.interpolate = (this.isStoredFlagOn(interpolate) || this.graphType === 'profile') ? 'interpolation' : '';
    this.disableMultiAnnual = this.graphType === 'profile';

    const multiAnnualMeanKey = this.graphService.getDataTransformMultiAnnualMeanKey(this.componentContext);
    const multiAnnualMean = this.graphService.getLocalStorageSelectedVariables(multiAnnualMeanKey);
    this.multiAnnualMean = (this.isStoredFlagOn(multiAnnualMean) && this.graphType !== 'profile') ? 'multiAnnualMean' : '';
  }

  onSelectInterpolate(): void {
    const interpolate = this.interpolate ? '1' : '';
    const interpolateKey = this.graphService.getDataTransformInterpolateKey(this.componentContext);
    this.graphService.setLocalStorageSelectedVariables(interpolateKey, interpolate);
    this.graphService.graphDataTransformerInterpolateSubject.next(Boolean(interpolate));
  }

  onSelectMultiAnnualMean(): void {
    const multiAnnualMean = this.multiAnnualMean ? '1' : '';
    const multiAnnualMeanKey = this.graphService.getDataTransformMultiAnnualMeanKey(this.componentContext);
    this.graphService.setLocalStorageSelectedVariables(multiAnnualMeanKey, multiAnnualMean);
    this.graphService.graphDataTransformerMultiAnnualMeanSubject.next(Boolean(multiAnnualMean));
  }

  onSelectDataTransform(): void {
    const dataTransform = this.dataTransform ? this.dataTransform : '';
    const dataTransformKey = this.graphService.getDataTransformGraphKey(this.componentContext);
    this.graphService.setLocalStorageSelectedVariables(dataTransformKey, dataTransform);
    this.graphService.graphDataTransformersSubject.next(dataTransform);
  }

  subscribeToGraphVariables(): void {
    this.graphVariablesSubscription = this.graphService.graphVariablesSubject.subscribe(graphVariables => {
      this.graphVariables = graphVariables;
    })
  }

  subscribeToGraphType(): void {
    this.graphTypeSubscription = this.graphService.graphTypeSubject.subscribe(graphType => {
      this.graphType = graphType;
      if (this.graphType === 'profile') {
        this.interpolate = 'interpolation';
        this.disableMultiAnnual = true;
        this.multiAnnualMean = '';
      } else {
        this.disableMultiAnnual = false;
        const multiAnnualMeanKey = this.graphService.getDataTransformMultiAnnualMeanKey(this.componentContext);
        const multiAnnualMean = this.graphService.getLocalStorageSelectedVariables(multiAnnualMeanKey);
        this.multiAnnualMean = (this.isStoredFlagOn(multiAnnualMean) && this.graphType !== 'profile') ? 'multiAnnualMean' : '';
      }
    })
  }


  ngOnDestroy(): void {
    if (this.graphVariablesSubscription) {
      this.graphVariablesSubscription.unsubscribe();
    }
    if (this.graphTypeSubscription) {
      this.graphTypeSubscription.unsubscribe();
    }
  }

}
